import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  NotebookPen,
  Check,
  Rocket,
  X,
  Repeat2,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/context/AuthContext";
import { FullScreenLoader } from "@/components/FullScreenLoader";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { AddFriendButton } from "@/components/AddFriendButton";

/* Session Review — practice reflection + post-call CTAs.
   Top-left: 60s auto-Next-Match countdown pill (like the original design).
   Cancel stops the countdown and swaps the pill for a "Back to Home" link.
   Rematch is server-signalled via POST /api/rematch/request (peer polls to
   pair up). Notes card is never obscured by a modal. */

const NOTES_MAX_LEN = 5000;
const REMATCH_POLL_MS = 2500;
const AUTO_NEXT_SECONDS = 60;

const formatDuration = (sec) => {
  const s = Math.max(0, Math.floor(Number(sec) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
  return `${m}:${String(ss).padStart(2, "0")}`;
};

const formatDateTime = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso || "";
  }
};

export default function SessionReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [session, setSession] = useState(null);
  const [error, setError] = useState(false);

  const fresh = !!location.state?.fresh;
  const mode = location.state?.mode === "friend" ? "friend" : "matchmaking";
  const rematchDest = mode === "friend" ? "/practice" : "/matchmaking";

  // Notes editor state — hydrated from server on first fetch.
  const [notes, setNotes] = useState("");
  const [savedNotes, setSavedNotes] = useState("");
  const [saving, setSaving] = useState(false);

  // Rematch state: idle | waiting (I requested) | matched (transitioning).
  const [rematchState, setRematchState] = useState("idle");
  const rematchCancelledRef = useRef(false);

  // Auto-Next-Match countdown — only runs while `fresh=true` and nothing has
  // been chosen yet. Cancel stops it, Rematch/Next-Match short-circuit it.
  const [countdown, setCountdown] = useState(fresh ? AUTO_NEXT_SECONDS : 0);
  const [countdownActive, setCountdownActive] = useState(fresh);
  const autoActedRef = useRef(false);

  // Friend button now handled entirely by <AddFriendButton /> — it fetches its
  // own status against /friends/status/{user_id}, so both here and in-call
  // stay perfectly in sync.
  const canAddFriend = !!session?.partner_user_id;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/sessions/${id}`);
        if (!alive) return;
        setSession(r.data);
        setNotes(r.data?.notes || "");
        setSavedNotes(r.data?.notes || "");
      } catch {
        if (alive) setError(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [id]);

  const peerId = session?.partner_user_id || null;
  const canRematch = fresh && !!peerId;

  // Poll rematch/status while we're waiting.
  useEffect(() => {
    if (rematchState !== "waiting" || !peerId) return;
    let alive = true;
    const tick = async () => {
      if (!alive || rematchCancelledRef.current) return;
      try {
        const r = await api.get(`/rematch/status?peer_user_id=${encodeURIComponent(peerId)}`);
        if (!alive) return;
        if (r.data?.status === "matched" && r.data?.room_name) {
          setRematchState("matched");
          navigate("/video-call", {
            replace: true,
            state: {
              // Carry the ended call's mode forward — Next Match after the
              // rematch must route to /matchmaking for normal calls, and
              // /practice only when the original call was a friend call.
              mode,
              room: r.data.room_name,
              // Preserve the mode of the call that just ended, and carry
              // the fairly-rotated role for the opening indicator.
              cameraOn: session?.call_mode !== "phone",
              yourRole: r.data.your_role || null,
            },
          });
        }
      } catch {
        /* ignore transient errors */
      }
    };
    tick();
    const iv = setInterval(tick, REMATCH_POLL_MS);
    return () => {
      alive = false;
      clearInterval(iv);
    };
  }, [rematchState, peerId, navigate, session?.call_mode]);

  // Best-effort cancel of any pending rematch when the user leaves this page.
  useEffect(() => {
    return () => {
      if (rematchState === "waiting" && peerId) {
        rematchCancelledRef.current = true;
        // Fire-and-forget — don't await during unmount.
        api.post("/rematch/cancel", { peer_user_id: peerId }).catch(() => {});
      }
    };
  }, [rematchState, peerId]);

  const dirty = notes !== savedNotes;

  const saveNotes = async () => {
    if (saving || !dirty) return;
    setSaving(true);
    try {
      const r = await api.patch(`/sessions/${id}`, { notes });
      setSession(r.data);
      setSavedNotes(r.data?.notes || "");
      toast.success("Notes saved.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not save notes. Try again.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const clickRematch = async () => {
    if (!canRematch || rematchState !== "idle") return;
    setRematchState("waiting");
    try {
      const r = await api.post("/rematch/request", { peer_user_id: peerId });
      if (r.data?.status === "matched" && r.data?.room_name) {
        setRematchState("matched");
        navigate("/video-call", {
          replace: true,
          state: {
            // Carry the ended call's mode forward (see poll tick above).
            mode,
            room: r.data.room_name,
            cameraOn: session?.call_mode !== "phone",
            yourRole: r.data.your_role || null,
          },
        });
      }
    } catch {
      toast.error("Could not request rematch.");
      setRematchState("idle");
    }
  };

  const clickCancel = async () => {
    // Cancel only stops the countdown — it does NOT navigate away.
    setCountdownActive(false);
    setCountdown(0);
    if (rematchState === "waiting" && peerId) {
      try { await api.post("/rematch/cancel", { peer_user_id: peerId }); } catch { /* ignore */ }
      setRematchState("idle");
    }
  };

  const clickNextMatch = async () => {
    setCountdownActive(false);
    autoActedRef.current = true;
    if (rematchState === "waiting" && peerId) {
      try { await api.post("/rematch/cancel", { peer_user_id: peerId }); } catch { /* ignore */ }
    }
    navigate(rematchDest, { replace: true });
  };

  const clickBackHome = () => navigate("/homepage", { replace: true });

  // Auto-Next-Match countdown effect (client-side timer only).
  useEffect(() => {
    if (!countdownActive) return;
    const iv = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(iv);
          if (!autoActedRef.current) {
            autoActedRef.current = true;
            navigate(rematchDest, { replace: true });
          }
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [countdownActive, navigate, rematchDest]);

  // Any rematch action also stops the countdown so we don't yank the user
  // away mid-decision.
  useEffect(() => {
    if (rematchState !== "idle") setCountdownActive(false);
  }, [rematchState]);

  if (error)
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#0A0A0A] text-white">
        <p className="text-zinc-400">Session not found.</p>
        <button
          data-testid="back-to-history-btn"
          onClick={() => navigate("/history")}
          className="mt-4 text-[#E52525]"
        >
          Back
        </button>
      </div>
    );
  if (!session) return <FullScreenLoader label="Loading review…" />;

  const waiting = rematchState === "waiting";

  return (
    <div className="flex min-h-screen bg-[#0A0A0A] font-body text-white">
      <DashboardSidebar active="" />

      <main
        className="flex-1 px-6 py-8 md:px-10"
        data-testid="session-review-page"
      >
      {/* Sticky top row — countdown pill or "Back to Home" link on the left,
          action buttons on the right. Only present right after a live call. */}
      {fresh && (
        <div
          data-testid="post-call-actionbar"
          className="sticky top-0 z-30 -mx-6 mb-8 flex flex-wrap items-center justify-between gap-3 border-b border-white/10 bg-[#0A0A0A]/95 px-6 py-4 backdrop-blur md:-mx-10 md:px-10"
        >
          {countdownActive ? (
            <span
              data-testid="next-match-countdown"
              className="inline-flex items-center gap-2 rounded-full border border-[#E52525]/30 bg-[#E52525]/10 px-3.5 py-2 text-xs font-semibold text-white"
            >
              <Rocket size={13} className="text-[#E52525]" />
              Next match in{" "}
              <span className="font-mono font-bold text-[#E52525]">{countdown}s</span>
            </span>
          ) : (
            <button
              type="button"
              data-testid="back-home-btn"
              onClick={clickBackHome}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-400 transition-colors hover:text-white"
            >
              <ArrowLeft size={16} /> Back to Home
            </button>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {countdownActive && (
              <button
                type="button"
                data-testid="post-call-cancel-btn"
                onClick={clickCancel}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-zinc-300 transition-colors hover:border-white/25 hover:text-white"
              >
                <X size={13} /> Cancel
              </button>
            )}
            <button
              type="button"
              data-testid="post-call-rematch-btn"
              onClick={clickRematch}
              disabled={!canRematch || waiting}
              title={canRematch ? "Play another round with the same partner" : "Rematch unavailable — no partner detected"}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#E52525] px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#c81f1f] disabled:cursor-not-allowed disabled:opacity-40"
            >
              {waiting ? (
                <>
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white/70" />
                  Waiting for partner…
                </>
              ) : (
                <>
                  <Repeat2 size={13} /> Rematch
                </>
              )}
            </button>
            <button
              type="button"
              data-testid="post-call-next-btn"
              onClick={clickNextMatch}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#E52525]/40 bg-[#E52525]/10 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#E52525]/20"
            >
              <Rocket size={13} /> Next match
            </button>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-3xl">
        {!fresh && (
          <button
            data-testid="back-to-history-btn"
            onClick={() => navigate("/history")}
            className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-zinc-400 transition-colors hover:text-white"
          >
            <ArrowLeft size={16} /> Back
          </button>
        )}

        <header className="mb-8">
          <div className="flex flex-wrap items-center gap-3">
            <h1
              data-testid="session-partner-name"
              className="font-heading text-3xl font-black tracking-tight sm:text-4xl"
            >
              Session with {session.partner_name || "Practice Partner"}
            </h1>
            {canAddFriend && (
              <AddFriendButton
                peerUserId={session.partner_user_id}
                peerUsername={session.partner_name}
                testid="review-add-friend-btn"
              />
            )}
          </div>
          <p className="mt-2 text-sm text-zinc-400">
            <span data-testid="session-datetime">{formatDateTime(session.created_at)}</span>
            <span className="mx-2 text-zinc-700">•</span>
            <span data-testid="session-duration">
              {formatDuration(session.duration_seconds)}
            </span>
          </p>
        </header>

        {/* Reflection editor */}
        <section
          data-testid="notes-card"
          className="rounded-2xl border border-white/10 bg-[#141414] p-6"
        >
          <div className="mb-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#E52525]/10 text-[#E52525]">
                <NotebookPen size={16} />
              </span>
              <div>
                <h2 className="font-heading text-base font-bold text-white">
                  Your notes
                </h2>
                <p className="text-xs text-zinc-500">
                  Capture your biggest takeaways while the call is fresh.
                </p>
              </div>
            </div>
            <span
              data-testid="notes-count"
              className={`hidden shrink-0 text-[11px] font-medium sm:block ${
                notes.length > NOTES_MAX_LEN - 200 ? "text-[#E52525]" : "text-zinc-500"
              }`}
            >
              {notes.length}/{NOTES_MAX_LEN}
            </span>
          </div>

          <textarea
            data-testid="notes-textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value.slice(0, NOTES_MAX_LEN))}
            placeholder="What worked? What didn't? What will you try next time?"
            rows={12}
            className="min-h-[280px] w-full resize-y rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm leading-relaxed text-white placeholder:text-zinc-600 focus:border-[#E52525]/50 focus:outline-none"
          />

          <div className="mt-4 flex items-center justify-between gap-3">
            <span data-testid="notes-status" className="text-xs text-zinc-500">
              {dirty
                ? "Unsaved changes"
                : savedNotes
                  ? (
                    <span className="inline-flex items-center gap-1 text-zinc-400">
                      <Check size={12} className="text-[#22C55E]" /> Notes saved
                    </span>
                  )
                  : "No notes yet"}
            </span>
            <button
              type="button"
              data-testid="save-notes-btn"
              onClick={saveNotes}
              disabled={saving || !dirty}
              className="inline-flex items-center gap-2 rounded-full bg-[#E52525] px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#c81f1f] disabled:cursor-not-allowed disabled:opacity-40"
            >
              {saving ? "Saving…" : "Save notes"}
            </button>
          </div>
        </section>
      </div>
      </main>
    </div>
  );
}
